import { onAuthDialogClose, openAuthDialog } from './auth-dialog.store';
import { clearSupabaseAuthStorage, supabase } from './supabase';

let isAuthDialogOpen = false;

// 对话框关闭时重置防重入标志
onAuthDialogClose(() => {
  isAuthDialogOpen = false;
});

const getUnauthorizedMessage = async (res: Response) => {
  try {
    const cloned = res.clone();
    const payload = await cloned.json();
    return payload?.error?.message || payload?.message || '请先登录';
  } catch {
    return '请先登录';
  }
};

const isSameOriginRequestUrl = (url: string) => {
  if (url.startsWith('/')) {
    return true;
  }
  try {
    return new URL(url, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
};

/** 为同源请求统一补齐 credentials（供全局 fetch 使用） */
export const applyAuthToFetchArgs = async (
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<[RequestInfo | URL, RequestInit | undefined]> => {
  if (typeof window === 'undefined') {
    return [input, init];
  }

  const url =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : input.url;

  if (!isSameOriginRequestUrl(url)) {
    return [input, init];
  }

  const headers = new Headers();

  if (input instanceof Request) {
    input.headers.forEach((value, key) => headers.set(key, value));
  }
  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => headers.set(key, value));
  }
  const credentials =
    init?.credentials ??
    (input instanceof Request ? input.credentials : undefined) ??
    'include';

  const nextInit: RequestInit = {
    ...init,
    headers,
    credentials,
  };

  return [input, nextInit];
};

export const handleUnauthorizedResponse = async (res: Response) => {
  if (typeof window === 'undefined' || res.status !== 401) {
    return;
  }

  if (isAuthDialogOpen) {
    return;
  }

  isAuthDialogOpen = true;
  const message = await getUnauthorizedMessage(res);
  await (supabase.auth.signOut as any)({ scope: 'local' }).catch(() => {});
  clearSupabaseAuthStorage();

  openAuthDialog({
    message,
    onSuccess: () => {
      isAuthDialogOpen = false;
      window.location.reload();
    },
  });
};

const throwForResponse = async (res: Response) => {
  if (res.ok) {
    return;
  }

  if (res.status === 401) {
    await handleUnauthorizedResponse(res);
  }

  throw new Error(await getUnauthorizedMessage(res));
};

export const createAuthFetcher = () => {
  return async (url: string, options?: RequestInit) => {
    const headers: HeadersInit = {
      ...options?.headers,
    };

    const res = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });

    await throwForResponse(res);

    return res.json();
  };
};

export const authFetcher = async (url: string) => {
  const res = await fetch(url, {
    credentials: 'include',
  });

  await throwForResponse(res);

  return res.json();
};

export const authFetch = async (url: string, options?: RequestInit) => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options?.headers,
  };

  const res = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (res.status === 401) {
    await handleUnauthorizedResponse(res);
  }

  return res;
};
